import { FC } from 'react';

import { PunchupFileDropZone } from 'features/common/components/Punchup/primitives/upload/PunchupFileDropZone';
import { PunchupButton } from 'features/common/components/Punchup/primitives/buttons/PunchupButton';
import { UseGuestListUploadReturn } from 'features/PunchupPages/CompedTickets/SendCompedTicketsModal';
import { SendPoolBudget } from 'features/PunchupPages/SendTickets/poolBudget';

import { BulkUploadTable } from '../BulkUploadTable';
import { BulkColumnMapping } from '../BulkColumnMapping';
import styles from './PunchupBulkUploadPanel.module.css';

export interface PunchupBulkUploadPanelProps {
	uploadState: UseGuestListUploadReturn;
	/** Shared-pool budget — threaded to the table so pooled rows are hard-capped. Omitted for pool-less events. */
	poolBudget?: SendPoolBudget;
	/** Pool ids over their sellable capacity (page-level `getExceededPools`) — a member row's stepper turns red. */
	exceededPoolIds?: Set<number>;
}

export const PunchupBulkUploadPanel: FC<PunchupBulkUploadPanelProps> = ({
	uploadState,
	poolBudget,
	exceededPoolIds,
}) => {
	const {
		fileData,
		ticketTypes,
		editableRows,
		originalHeaders,
		columnMapping,
		setColumnMappingField,
		getExceededTicketTypes,
		handleFile,
		handleDeleteFile,
		updateRow,
		addRow,
		deleteRow,
	} = uploadState;

	const fileLoaded = !!fileData && !fileData.isProcessing && !fileData.errors;

	const fileInfo = fileLoaded ? `${editableRows.length} rows` : undefined;

	const exceededTypeIds = new Set(getExceededTicketTypes().map((tt) => tt.iD));

	// Removing the file clears the rows in the shared hook; re-seed one empty row
	// so the editable table (the primary input) stays visible — the file is only
	// one way to fill it. Mirrors the page-level seed on entering bulk mode.
	const handleRemoveFile = () => {
		handleDeleteFile();
		addRow();
	};

	return (
		<div className={styles.panel}>
			{/* The editable table is the primary input and sits directly below the
			    radio cards. It hides only when every row has been individually
			    deleted (the "+ Add row" button below stays to bring it back). */}
			{editableRows.length > 0 && (
				<BulkUploadTable
					rows={editableRows}
					ticketTypes={ticketTypes}
					exceededTypeIds={exceededTypeIds}
					poolBudget={poolBudget}
					exceededPoolIds={exceededPoolIds}
					onUpdate={updateRow}
					onDeleteRow={deleteRow}
				/>
			)}

			{/* Add-row is always available — the table is usable without a file;
			    the file is just one way to fill it (invariants 11/12: the button
			    lives in the panel, never inside BulkUploadTable). */}
			<PunchupButton variant="outline" type="button" onClick={addRow}>
				+ Add row
			</PunchupButton>

			{/* Import-from-file section — one way to fill the table above. The drop
			    zone, Format details, and (for multi-column files) column matching
			    all belong to this secondary "fill from a file" affordance. */}
			<div className={styles.uploadSection}>
				<div className={styles.uploadHeader}>
					<h3 className={styles.uploadTitle}>Import from a file</h3>
					<p className={styles.uploadDescription}>
						Drop a CSV or Excel file to fill the table above — we match your columns by name.
					</p>
				</div>

				<PunchupFileDropZone
					accept=".csv,.xlsx,.xls"
					onFileSelect={handleFile}
					file={fileData?.file || null}
					fileInfo={fileInfo}
					isProcessing={fileData?.isProcessing}
					error={fileData?.errors}
					onRemove={handleRemoveFile}
				/>

				<details className={styles.formatDetails}>
					<summary className={styles.formatSummary}>Format details</summary>
					<ul className={styles.formatList}>
						<li>
							Only <strong>Email</strong> is required — other columns are optional.
						</li>
						<li>Column names are matched case-insensitively; order does not matter.</li>
						<li>
							<strong>Ticket Type</strong> is matched by name to available types for this event.
						</li>
						<li>
							<strong>Quantity</strong> defaults to 1 if the column is missing or empty.
						</li>
						<li>Single-column files are treated as email lists with no other data.</li>
					</ul>
				</details>

				{/* Column matching — only for multi-column files; single-column / manual
				    entry has no headers to map. Lets the manager upload arbitrary headers. */}
				{originalHeaders.length > 1 && columnMapping && (
					<BulkColumnMapping
						originalHeaders={originalHeaders}
						mapping={columnMapping}
						onChange={setColumnMappingField}
					/>
				)}
			</div>
		</div>
	);
};
